import Avatar from "./basic-6-avatar.jpg";
import LogoPlaceholder from "./logo-placeholder.png";
import Slide1 from "./preview/slide-1.png";
import Slide2 from "./preview/slide-2.png";
import Slide3 from "./preview/slide-3.png";
import Slide4 from "./preview/slide-4.png";
import Slide5 from "./preview/slide-5.png";
import BgOption1 from "./../images/bg-option-1.png";
import BgOption2 from "./../images/bg-option-2.png";
import BgOption3 from "./../images/bg-option-3.png";
import { MultilineView, TextView } from "../../components";
import BaseTemplate from "../base";

class Basic6 extends BaseTemplate {
  constructor() {
    super();
    this.name = "Basic #6";
    this.id = "7";
    this.url = "/ai/carousels/basic-6";
    this.description =
      "Create a simple and sleek carousel without the need for your image";
    this.common_settings = {
      elements: [
        {
          label: "Your Name",
          type: "text",
          value: "Aliah Lane",
        },
        {
          label: "Profile Pic",
          type: "logo",
          key: "logo",
          value: {
            type: "image",
            image: Avatar,
          },
        },
        {
          label: "Handle",
          type: "text",
          value: "@alane",
        },
        {
          label: "Background",
          type: "background",
          key: "background_color",
          value: {
            type: "color",
            image: "",
            color: "#db2977",
            color_options: [
              "#2cd4bf",
              "#38bcf7",
              "#feb022",
              "#f87066",
              "#33d583",
              "#818cf8",
              "#f471b6",
              "#bf84fc",
            ],
            image_options: [
              {
                image: BgOption1,
              },
              {
                image: BgOption2,
              },
              {
                image: BgOption3,
              },
            ],
          },
        },
        {
          label: "Primary Color",
          type: "color",
          key: "primary_color",
          style_key_group: "primary",
          value: "#000000",
        },
        {
          label: "Secondary Color",
          type: "color",
          key: "secondar_color",
          style_key_group: "secondary",
          value: "#ffffff",
        },
        {
          label: "Primary Font",
          key: "primary_font",
          value: "Zilla Slab",
          type: "font",
        },
      ],
      swipe_indicator: {
        image: "",
      },
    };
    this.preview_images = [Slide1, Slide2, Slide3, Slide4, Slide5];
    this.slides = [
      {
        elements: [
          {
            label: "Title",
            target_key: "title",
            type: "text",
            value: "How to build your personal brand on social media",
            max_words: 8,
            style: {
              color: {
                key: "background_color",
              },
              fontSize: {
                value: "48px",
              },
              fontFamily: {
                key: "primary_font",
              },
              fontWeight: {
                value: "bold",
              },
              textAlign: {
                value: "center",
              },
            },
          },
        ],
        slide_type: "intro",
      },
      {
        elements: [
          {
            label: "Description",
            target_key: "description",
            type: "multiline",
            value: `Make sure your social media accounts are updated with your current information and pictures.
            `,
            max_words: 25,
            style: {
              color: {
                key: "primary_color",
              },
              fontSize: {
                value: "30px",
              },
              fontFamily: {
                key: "primary_font",
              },
              fontWeight: {
                value: "bold",
              },
              textAlign: {
                value: "center",
              },
            },
          },
        ],
        slide_type: "middle",
      },
      {
        elements: [
          {
            label: "Description",
            target_key: "description",
            type: "multiline",
            value: `Create different types of content for each social media platform and remember to schedule them for ease.
            `,
            max_words: 25,
            style: {
              color: {
                key: "primary_color",
              },
              fontSize: {
                value: "30px",
              },
              fontFamily: {
                key: "primary_font",
              },
              fontWeight: {
                value: "bold",
              },
              textAlign: {
                value: "center",
              },
            },
          },
        ],
        slide_type: "middle",
      },
      {
        elements: [
          {
            label: "Description",
            target_key: "description",
            type: "multiline",
            value: `Ensure that all your social media channels share the same brand voice and messaging.
            `,
            max_words: 25,
            style: {
              color: {
                key: "primary_color",
              },
              fontSize: {
                value: "30px",
              },
              fontFamily: {
                key: "primary_font",
              },
              fontWeight: {
                value: "bold",
              },
              textAlign: {
                value: "center",
              },
            },
          },
        ],
        slide_type: "middle",
      },
      {
        elements: [
          {
            label: "Title",
            target_key: "title",
            type: "text",
            value: "Try it out & let us know your feedback",
            max_words: 7,
          },
          {
            label: "CTA Text",
            type: "text",
            target_key: "cta_text",
            value: "Comment below",
            max_words: 2,
          },
        ],
        slide_type: "outro",
      },
    ];
  }

  setCommonSettings(settings) {
    this.common_settings = settings;
  }

  getPrimaryColor(common_settings) {
    return common_settings.elements[4]?.value;
  }

  getBackgroundColor(common_settings) {
    return common_settings.elements[3]?.value.color;
  }

  getBackgroundColorVariant(common_settings) {
    return this.getBackgroundColor(common_settings) + "20";
  }

  getBackgroundColorVariantSecond(common_settings) {
    return this.getBackgroundColor(common_settings) + "40";
  }

  getSecondaryColor(common_settings) {
    return common_settings.elements[5]?.value;
  }

  getPrimaryFont(common_settings) {
    return common_settings.elements[6]?.value || "Zilla Slab";
  }

  duplicateSlide(slide, index) {
    let new_slide = JSON.parse(JSON.stringify(slide));
    this.slides.splice(index + 1, 0, new_slide);
    return new_slide;
  }

  renderOutroSlide(
    slide,
    index,
    currentSlide,
    carouselLength,
    commonSettings,
    updateSlideElementPosition,
    updateSlideElementSize,
    setCurrentSlideElement,
    previewMode
  ) {
    return (
      <>
        {index === carouselLength - 1 && (
          <>
            <div className="absolute inset-x-0 grid w-full h-full place-items-center">
              <div className="text-center flex flex-col items-center">
                <img
                  className="object-cover rounded-full w-36 h-36"
                  src={
                    commonSettings.elements[1].value.image
                      ? commonSettings.elements[1].value.image
                      : LogoPlaceholder
                  }
                  alt=""
                />
                <p
                  className="mt-4 text-3xl font-bold tracking-tight"
                  style={{ color: this.getPrimaryColor(commonSettings) }}
                >
                  <TextView text={commonSettings.elements[0]?.value} />
                </p>
                <p
                  className="text-xl font-normal"
                  style={{ color: this.getPrimaryColor(commonSettings) }}
                >
                  <TextView text={commonSettings.elements[2]?.value} />
                </p>
              </div>
            </div>
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-center px-12 pb-8">
              <svg
                className="w-auto h-5"
                style={{
                  color: this.getBackgroundColor(commonSettings),
                }}
                viewBox="0 0 97 20"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M20 10.2712C19.9397 12.8953 18.89 15.3644 16.7644 17.3554C15.7925 18.266 14.6695 18.939 13.4193 19.3927C12.0507 19.8902 10.5899 20.0823 9.1393 19.9556C7.68869 19.8289 6.2833 19.3865 5.02156 18.6593C4.92447 18.6034 4.846 18.621 4.75437 18.6546C3.57485 19.0846 2.39532 19.5164 1.21407 19.9405C0.863685 20.0662 0.540869 19.9902 0.276656 19.7283C0.00871841 19.4627 -0.0672676 19.1373 0.060369 18.7813C0.489467 17.5833 0.923034 16.3869 1.36107 15.1922C1.37514 15.1585 1.38069 15.1218 1.37723 15.0855C1.37376 15.0491 1.36139 15.0142 1.34121 14.9838C0.912669 14.2468 0.582648 13.4568 0.359595 12.6339C0.0714628 11.5907 -0.0428892 10.5072 0.0211344 9.42675C0.151585 7.27454 0.89398 5.36001 2.24832 3.68316C3.33422 2.34155 4.68831 1.35199 6.29222 0.712238C7.66774 0.16054 9.14935 -0.0757251 10.6281 0.0208109C14.4555 0.256338 17.8163 2.67571 19.2632 6.24142C19.7514 7.44365 19.9898 8.69656 20 10.2712Z" />
                <path d="M55.2253 0.454346C52.1855 0.454346 50.5003 2.62872 50.5003 2.62872C50.5003 2.62872 48.815 0.454346 45.7753 0.454346C42.5854 0.454346 40.0002 3.01574 40.0002 6.17639C40.0002 10.5158 45.1579 14.721 46.5953 16.0495C48.2512 17.5789 50.5003 19.5452 50.5003 19.5452C50.5003 19.5452 52.7494 17.5789 54.4052 16.0495C55.8427 14.721 61.0003 10.5158 61.0003 6.17639C61.0003 3.01574 58.4152 0.454346 55.2253 0.454346Z" />
                <path d="M96.2734 18.415C96.2734 18.884 95.7403 19.1541 95.3622 18.8766L88.9757 14.1909C88.7742 14.043 88.4999 14.043 88.2984 14.1909L81.9119 18.8766C81.5338 19.1541 81.0007 18.884 81.0007 18.415V1.02686C81.0007 0.710667 81.2571 0.454346 81.5732 0.454346H95.7009C96.0171 0.454346 96.2734 0.710668 96.2734 1.02686V18.415Z" />
              </svg>
            </div>
          </>
        )}
      </>
    );
  }

  renderMiddleSlide(
    slide,
    index,
    currentSlide,
    carouselLength,
    commonSettings,
    updateSlideElementPosition,
    updateSlideElementSize,
    setCurrentSlideElement,
    previewMode
  ) {
    return (
      <>
        {index !== carouselLength - 1 && index !== 0 && (
          <>
            <div className="px-8 pt-16">
              <div className="relative">
                <div className="absolute -translate-x-1/2 -top-7 left-1/2">
                  <div
                    className="inline-flex items-center justify-center text-2xl font-bold rounded-full w-14 h-14"
                    style={{
                      color: this.getBackgroundColor(commonSettings),
                      backgroundColor: this.getSecondaryColor(commonSettings),
                      border: `8px solid ${this.getBackgroundColorVariant(
                        commonSettings
                      )}`,
                    }}
                  >
                    {index < 10 ? `0${index}` : index}
                  </div>
                </div>
                <div
                  className="px-6 pt-12 pb-6 rounded-2xl"
                  style={{
                    backgroundColor: this.getSecondaryColor(commonSettings),
                    boxShadow: `0px 8px 0px ${this.getBackgroundColorVariantSecond(
                      commonSettings
                    )}`,
                  }}
                >
                  <div
                    className="text-3xl font-bold text-center"
                    style={{
                      color: this.getPrimaryColor(commonSettings),
                    }}
                  >
                    {slide.elements[0]?.style ? (
                      <MultilineView
                        element={slide.elements[0]}
                        getPropertyByStyleKey={(styleKey) =>
                          this.getPropertyByStyleKey(styleKey, commonSettings)
                        }
                        getValueByKey={(key) =>
                          this.getValueByKey(key, commonSettings)
                        }
                      />
                    ) : (
                      <TextView text={slide.elements[0]?.value} />
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-center px-12 pb-8">
              <p
                className="text-base font-medium"
                style={{ color: this.getPrimaryColor(commonSettings) }}
              >
                <TextView text={commonSettings.elements[2]?.value} />
              </p>
            </div>
          </>
        )}
      </>
    );
  }

  renderIntroSlide(
    slide,
    index,
    currentSlide,
    carouselLength,
    commonSettings,
    updateSlideElementPosition,
    updateSlideElementSize,
    setCurrentSlideElement,
    previewMode
  ) {
    return (
      <>
        {index === 0 && (
          <>
            <div className="absolute inset-x-0 top-0 flex items-center justify-center gap-3 px-12 pt-10">
              <img
                className="object-cover w-8 h-8 rounded-full"
                src={
                  commonSettings.elements[1].value.image
                    ? commonSettings.elements[1].value.image
                    : LogoPlaceholder
                }
                alt=""
              />
              <p
                className="text-2xl font-bold"
                style={{ color: this.getPrimaryColor(commonSettings) }}
              >
                {<TextView text={commonSettings.elements[0]?.value} />}
              </p>
            </div>
            <div className="absolute inset-0 grid p-10 text-center place-items-center">
              <div
                className="text-5xl font-bold"
                style={{ color: this.getBackgroundColor(commonSettings) }}
              >
                {slide.elements[0]?.style ? (
                  <MultilineView
                    element={slide.elements[0]}
                    getPropertyByStyleKey={(styleKey) =>
                      this.getPropertyByStyleKey(styleKey, commonSettings)
                    }
                    getValueByKey={(key) =>
                      this.getValueByKey(key, commonSettings)
                    }
                  />
                ) : (
                  <TextView text={slide.elements[0]?.value} />
                )}
              </div>
            </div>
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-center px-12 pb-8">
              <p
                className="text-base font-medium"
                style={{ color: this.getPrimaryColor(commonSettings) }}
              >
                <TextView text={commonSettings.elements[2]?.value} />
              </p>
            </div>
          </>
        )}
      </>
    );
  }

  renderSlide(
    slide,
    index,
    currentSlide,
    carouselLength,
    commonSettings,
    updateSlideElementPosition,
    updateSlideElementSize,
    setCurrentSlideElement,
    previewMode
  ) {
    return (
      <div
        key={index}
        className={
          "carousel-slide-container ring-1 ring-gray-200 bg-white relative shrink-0 aspect-[4/5] flex flex-col w-[400px] h-[500px] overflow-hidden " +
          (index !== currentSlide ? "hidden" : "") +
          " index-" +
          index
        }
        style={{
          fontFamily: this.getPrimaryFont(commonSettings),
        }}
      >
        <div className="absolute inset-0">
          {commonSettings.elements[3].value.type === "image" &&
            commonSettings.elements[3].value.image && (
              <img
                className="object-cover w-full h-full"
                src={commonSettings.elements[3].value.image}
                alt=""
              />
            )}
          {commonSettings.elements[3].value.type === "color" && (
            <div
              className="w-full h-full"
              style={{
                backgroundColor: this.getBackgroundColorVariant(commonSettings),
              }}
            ></div>
          )}
        </div>
        {index === 0 &&
          this.renderIntroSlide(
            slide,
            index,
            currentSlide,
            carouselLength,
            commonSettings,
            updateSlideElementPosition,
            updateSlideElementSize,
            setCurrentSlideElement,
            previewMode
          )}
        {index === carouselLength - 1 &&
          this.renderOutroSlide(
            slide,
            index,
            currentSlide,
            carouselLength,
            commonSettings,
            updateSlideElementPosition,
            updateSlideElementSize,
            setCurrentSlideElement,
            previewMode
          )}
        {index !== carouselLength - 1 &&
          index !== 0 &&
          this.renderMiddleSlide(
            slide,
            index,
            currentSlide,
            carouselLength,
            commonSettings,
            updateSlideElementPosition,
            updateSlideElementSize,
            setCurrentSlideElement,
            previewMode
          )}
      </div>
    );
  }
}

export default Basic6;
